import { Input, InputNumber } from "@/components/ui";
import {
  CrownOutlined,
  DeleteOutlined,
  PlusCircleFilled,
  TeamOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";

type Participant = {
  id: string;
  name: string;
  email: string;
  isWinner: boolean;
  registrantId?: string;
};

type Category = {
  id: string;
  name: string;
  prizeName: string;
  winnerCount: number;
  participants: Participant[];
};

type Props = {
  categories: Category[];
  onCategoryChange: (
    categoryId: string,
    field: keyof Category,
    value: string | number,
    persist?: boolean
  ) => void;
  onAddCategory: () => void;
  onDeleteCategory: (categoryId: string) => void;
  onDrawNow: (categoryId: string) => void;
  onAddAttendee: (categoryId: string) => void;
  onDeleteParticipant: (categoryId: string, participantId: string) => void;
  busyCategoryId?: string | null;
  addingCategory?: boolean;
  drawingCategoryId?: string | null;
};

type WinnerCountInputProps = {
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  onCommit: (value: number) => void;
};

// Keeps a local draft while typing so the value is only persisted on blur/enter.
// Persisting on every keystroke disables the input mid-typing and steals focus.
const WinnerCountInput = ({
  value,
  disabled,
  onChange,
  onCommit,
}: WinnerCountInputProps) => {
  const [draft, setDraft] = useState<number | null>(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = () => {
    const next = draft && draft > 0 ? draft : 1;
    setDraft(next);
    onCommit(next);
  };

  return (
    <InputNumber
      value={draft}
      size="small"
      onChange={(next) => {
        setDraft(next);
        if (next && next > 0) onChange(next);
      }}
      onBlur={commit}
      onPressEnter={commit}
      onStep={(next) => onCommit(next)}
      min={1}
      addonBefore={<TeamOutlined className="text-black/65" />}
      className="w-full lucky-draw-winner-count"
      disabled={disabled}
    />
  );
};

const DrawWinnersCard = ({
  categories,
  onCategoryChange,
  onAddCategory,
  onDeleteCategory,
  onDrawNow,
  onAddAttendee,
  onDeleteParticipant,
  busyCategoryId,
  addingCategory,
  drawingCategoryId,
}: Props) => {
  const isBusy = (categoryId: string) => busyCategoryId === categoryId;

  return (
    <div className="bg-white border border-[#EBEBEB] overflow-hidden flex flex-col col-span-2">
      {/* Header */}
      <div className="px-8 py-5 bg-white border-b border-[#EBEBEB] flex items-center gap-3">
        <CrownOutlined className="text-xl text-black/85" />
        <span className="text-xl font-normal leading-6 text-black/85">
          Draw Winners
        </span>
      </div>

      {/* Categories */}
      {categories.map((category, index) => (
        <div key={category.id}>
          {/* Category Section */}
          <div className="p-8 flex flex-col items-center gap-5">
            {/* Category Header */}
            <div className="w-full flex items-start justify-between gap-5">
              <span className="flex-1 text-sm font-bold leading-[22px] text-black/85">
                Category {index + 1}
              </span>
              <button
                type="button"
                onClick={() => onDeleteCategory(category.id)}
                disabled={isBusy(category.id)}
                className="w-5 h-5 flex items-center justify-center text-black/85 hover:text-red-500 transition-colors"
                aria-label="Delete category"
                >
                <DeleteOutlined className="text-base" />
              </button>
            </div>

            {/* Input Row */}
            <div className="w-full flex items-start gap-5">
              {/* Category Input */}
              <div className="flex-1 flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-sm text-black/85">Category</span>
                  <span className="text-sm text-black/85">:</span>
                </div>
                <Input
                  value={category.name}
                  onChange={(e) =>
                    onCategoryChange(category.id, "name", e.target.value)
                  }
                  onBlur={(e) =>
                    onCategoryChange(category.id, "name", e.target.value, true)
                  }
                  placeholder="Grand Prize"
                  className="flex-1"
                  disabled={isBusy(category.id)}
                />
              </div>

              {/* Prize Input */}
              <div className="flex-1 flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-sm text-black/85">Prize</span>
                  <span className="text-sm text-black/85">:</span>
                </div>
                <Input
                  value={category.prizeName}
                  onChange={(e) =>
                    onCategoryChange(category.id, "prizeName", e.target.value)
                  }
                  onBlur={(e) =>
                    onCategoryChange(category.id, "prizeName", e.target.value, true)
                  }
                  placeholder="Macbook"
                  className="flex-1"
                  disabled={isBusy(category.id)}
                />
              </div>

              {/* Winner Count Input */}
              <div className="w-[100px]">
                <WinnerCountInput
                  value={category.winnerCount}
                  disabled={isBusy(category.id)}
                  onChange={(value) =>
                    onCategoryChange(category.id, "winnerCount", value)
                  }
                  onCommit={(value) =>
                    onCategoryChange(category.id, "winnerCount", value, true)
                  }
                />
              </div>
            </div>

            {/* Winners Box */}
            <div className="w-full p-4 rounded border border-[#E8E8E8] flex flex-col items-center gap-2.5">
              {/* Winners List */}
              {category.participants.map((participant) => (
                <div
                  key={participant.id}
                  className="w-full pb-1 border-b border-[#EBEBEB] flex items-center gap-2.5"
                >
                  <div className="flex-1 flex items-center gap-2.5">
                    <span className="text-sm text-black/85">
                      {participant.name}
                    </span>
                    <span className="text-[13px] text-black/45">
                      {participant.email}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        onDeleteParticipant(category.id, participant.id)
                      }
                      className="text-sm text-ava-blue-06 bg-transparent border-none cursor-pointer"
                    >
                      delete
                    </button>
                  </div>
                </div>
              ))}

              {/* Draw Now Button */}
              <button
                type="button"
                onClick={() => onDrawNow(category.id)}
                disabled={
                  category.participants.length >= category.winnerCount ||
                  drawingCategoryId === category.id ||
                  isBusy(category.id)
                }
                className="px-3 py-1 bg-white rounded-sm border border-gray-500 shadow-[0px_2px_0px_rgba(0,0,0,0.02)] text-sm text-black/85 hover:border-ava-blue-07 hover:text-ava-blue-07 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {drawingCategoryId === category.id ? "Drawing..." : "✨ Draw Now"}
              </button>

              {/* Add Attendee Button */}
              <button
                type="button"
                onClick={() => onAddAttendee(category.id)}
                className="mt-4 flex items-center gap-2.5 bg-white rounded-sm text-sm text-black/85 border-none cursor-pointer hover:text-ava-blue-07 transition-colors"
              >
                <PlusCircleFilled className="text-xl text-[#323232]" />
                <span>Add Attendee</span>
              </button>
            </div>
          </div>

          {/* Divider */}
          {index < categories.length - 1 && (
            <div className="w-full h-0 border-t border-[#EBEBEB]" />
          )}
        </div>
      ))}

      {/* Divider before Add Category */}
      {categories.length > 0 && (
        <div className="w-full h-0 border-t border-[#EBEBEB]" />
      )}

      {/* Add Category Section */}
      <div className="p-8 flex flex-col items-start gap-5">
        <button
          type="button"
          onClick={onAddCategory}
          disabled={addingCategory}
          className="flex items-center gap-2.5 bg-white rounded-sm text-sm text-black/85 border-none cursor-pointer hover:text-[#40a9ff] transition-colors"
        >
          <PlusCircleFilled className="text-xl text-[#323232]" />
          <span>{addingCategory ? "Adding..." : "Add Category"}</span>
        </button>
      </div>
    </div>
  );
};

export default DrawWinnersCard;
